import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookOpen,
  faCalendarDays,
  faCopy,
  faEnvelope,
  faShare,
  faShareNodes,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faTelegram,
  faTwitter,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import "./DailyVerse.css";
import { AuthContext } from "../../components/Authentication/AuthContext";
import {
  OLD_TESTAMENT_BOOKS,
  NEW_TESTAMENT_BOOKS,
  normalizeBookKey,
  getBookNameByLanguage,
  getEnglishBookName,
  getTamilBookName,
  getDeutschBookName,
} from "../Bible/BookNames";
import { getDailyVerseForToday, startDailyVerseCache } from "./VerseCashe";

const ALL_CANONICAL_BOOK_KEYS = [
  ...OLD_TESTAMENT_BOOKS,
  ...NEW_TESTAMENT_BOOKS,
];

const normalizeDailyVerseBookValue = (value) => {
  return String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[\s._\-:;,'’`´]+/g, "");
};

const DAILY_VERSE_BOOK_KEY_LOOKUP = ALL_CANONICAL_BOOK_KEYS.reduce(
  (lookup, bookKey) => {
    const possibleNames = [
      bookKey,
      normalizeBookKey(bookKey),
      getEnglishBookName(bookKey),
      getTamilBookName(bookKey),
      getDeutschBookName(bookKey),
    ];

    possibleNames.forEach((bookName) => {
      lookup[normalizeDailyVerseBookValue(bookName)] = bookKey;
    });

    return lookup;
  },
  {},
);

function DailyVerse() {
  const { language } = useContext(AuthContext);
  const [dailyVerse, setDailyVerse] = useState(null);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const shareButtonRef = useRef(null);
  const shareOptionsRef = useRef(null);

  const languageText = useMemo(() => {
    if (language === "English") {
      return {
        title: "Daily Verse",
        today: "Today",
        loading: "Loading verse...",
        share: "Share verse",
        copy: "Copy",
        copied: "Copied",
        gmail: "Gmail",
        facebook: "Facebook",
        whatsapp: "WhatsApp",
        twitter: "Twitter / X",
        telegram: "Telegram",
        other: "More",
        mailSubject: "Daily Verse",
      };
    }

    if (language === "Deutsch") {
      return {
        title: "Täglicher Vers",
        today: "Heute",
        loading: "Vers wird geladen...",
        share: "Vers teilen",
        copy: "Kopieren",
        copied: "Kopiert",
        gmail: "Gmail",
        facebook: "Facebook",
        whatsapp: "WhatsApp",
        twitter: "Twitter / X",
        telegram: "Telegram",
        other: "Mehr",
        mailSubject: "Täglicher Vers",
      };
    }

    return {
      title: "இன்றைய வேத வசனம்",
      today: "இன்று",
      loading: "வசனம் ஏற்றப்படுகிறது...",
      share: "வசனத்தை பகிரவும்",
      copy: "நகலெடு",
      copied: "நகலெடுக்கப்பட்டது",
      gmail: "Gmail",
      facebook: "Facebook",
      whatsapp: "WhatsApp",
      twitter: "Twitter / X",
      telegram: "Telegram",
      other: "மேலும்",
      mailSubject: "இன்றைய வேத வசனம்",
    };
  }, [language]);

  useEffect(() => {
    let isMounted = true;

    const fetchDailyVerse = async () => {
      setLoading(true);

      try {
        startDailyVerseCache();

        const selectedVerse = await getDailyVerseForToday();

        if (!isMounted) {
          return;
        }

        setDailyVerse(selectedVerse);
      } catch (error) {
        console.error("Error fetching daily verse:", error);

        if (isMounted) {
          setDailyVerse(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDailyVerse();

    return () => {
      isMounted = false;
    };
  }, [language]);

  useEffect(() => {
    if (!showShareOptions) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      const target = event.target;

      if (shareButtonRef.current && shareButtonRef.current.contains(target)) {
        return;
      }

      if (shareOptionsRef.current && shareOptionsRef.current.contains(target)) {
        return;
      }

      setShowShareOptions(false);
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setShowShareOptions(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    document.addEventListener("touchstart", handleOutsideClick, true);
    document.addEventListener("keydown", handleEscapeKey, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
      document.removeEventListener("touchstart", handleOutsideClick, true);
      document.removeEventListener("keydown", handleEscapeKey, true);
    };
  }, [showShareOptions]);

  const getVerseBook = () => {
    return (
      dailyVerse?.book || dailyVerse?.book_name || dailyVerse?.bookName || ""
    );
  };

  const getVerseChapter = () => {
    return (
      dailyVerse?.chapter ||
      dailyVerse?.chapter_number ||
      dailyVerse?.chapterNumber ||
      ""
    );
  };

  const getVerseNumber = () => {
    return (
      dailyVerse?.verse ||
      dailyVerse?.verse_number ||
      dailyVerse?.verseNumber ||
      ""
    );
  };

  const getCanonicalBookKey = (bookName) => {
    const normalizedBookName = normalizeDailyVerseBookValue(bookName);
    const mappedBookKey = DAILY_VERSE_BOOK_KEY_LOOKUP[normalizedBookName];

    if (mappedBookKey) {
      return mappedBookKey;
    }

    const compactBookKey = normalizeBookKey(bookName);

    if (ALL_CANONICAL_BOOK_KEYS.includes(compactBookKey)) {
      return compactBookKey;
    }

    return bookName;
  };

  const getBookName = (bookName) => {
    const canonicalBookKey = getCanonicalBookKey(bookName);

    return getBookNameByLanguage(canonicalBookKey, language);
  };

  const getVerseText = () => {
    if (!dailyVerse) {
      return "";
    }

    if (language === "English") {
      return dailyVerse.english || "";
    }

    if (language === "Deutsch") {
      return dailyVerse.deutsch || "";
    }

    return dailyVerse.tamil || "";
  };

  const getFormattedDate = () => {
    const today = new Date();

    if (language === "English") {
      return new Intl.DateTimeFormat("en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(today);
    }

    if (language === "Deutsch") {
      return new Intl.DateTimeFormat("de-DE", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(today);
    }

    return new Intl.DateTimeFormat("ta-LK", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(today);
  };

  const getReferenceText = () => {
    if (!dailyVerse) {
      return "";
    }

    return `${getBookName(getVerseBook())} ${getVerseChapter()}:${getVerseNumber()}`;
  };

  const getShareHeader = () => {
    return `📖 ${languageText.title}`;
  };

  const getShareText = () => {
    if (!dailyVerse) {
      return "";
    }

    return `${getShareHeader()}\n\n${getReferenceText()}\n${getVerseText()}`;
  };

  const getShareUrl = () => {
    const currentOrigin = window.location.origin || "";
    const currentHref = window.location.href || "";

    if (
      currentOrigin.includes("localhost") ||
      currentOrigin.includes("127.0.0.1")
    ) {
      return "https://www.tamilholybible.com";
    }

    return currentHref;
  };

  const closeShareDropdown = () => {
    setShowShareOptions(false);
  };

  const toggleShareOptions = () => {
    setShowShareOptions((previousValue) => !previousValue);
  };

  const copyTextToClipboard = async (text) => {
    if (!text) {
      return false;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "-9999px";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const copiedSuccessfully = document.execCommand("copy");

      document.body.removeChild(textArea);

      return copiedSuccessfully;
    } catch (error) {
      console.error("Copy failed:", error);
      return false;
    }
  };

  const copyVerse = async () => {
    const copiedSuccessfully = await copyTextToClipboard(getShareText());

    if (copiedSuccessfully) {
      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 1400);
    }

    closeShareDropdown();
  };

  const openShareWindow = (shareUrl) => {
    if (!shareUrl) {
      closeShareDropdown();
      return;
    }

    const shareWindow = window.open(shareUrl, "_blank", "noopener,noreferrer");

    if (shareWindow) {
      shareWindow.focus();
    }

    closeShareDropdown();
  };

  const shareOnSocialMedia = async (platform) => {
    if (!dailyVerse) {
      closeShareDropdown();
      return;
    }

    const text = getShareText();
    const sharePageUrl = getShareUrl();
    let shareUrl = "";

    if (platform === "facebook") {
      await copyTextToClipboard(text);

      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        sharePageUrl,
      )}&quote=${encodeURIComponent(text)}`;
    }

    if (platform === "whatsapp") {
      shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
        text,
      )}`;
    }

    if (platform === "gmail") {
      shareUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(
        languageText.mailSubject,
      )}&body=${encodeURIComponent(text)}`;
    }

    if (platform === "twitter") {
      shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text,
      )}`;
    }

    if (platform === "telegram") {
      shareUrl = `https://t.me/share/url?text=${encodeURIComponent(text)}`;
    }

    openShareWindow(shareUrl);
  };

  const shareWithDevice = async () => {
    if (!dailyVerse) {
      closeShareDropdown();
      return;
    }

    const text = getShareText();

    try {
      if (navigator.share) {
        await navigator.share({
          text,
        });
      } else {
        await copyTextToClipboard(text);
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Native share failed:", error);
      }
    } finally {
      closeShareDropdown();
    }
  };

  return (
    <div className="daily-verse-container">
      <article className="daily-verse-card">
        <div className="daily-verse-particles" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>

        <div className="daily-verse-header">
          <div className="daily-verse-title-wrap">
            <h3 className="daily-verse-title">
              <span className="daily-verse-title-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faBookOpen} />
              </span>
              <span>{languageText.title}</span>
            </h3>
          </div>

          <div className="daily-verse-header-right">
            <div className="daily-verse-date">
              <FontAwesomeIcon icon={faCalendarDays} />
              <span>{languageText.today}</span>
              <strong>{getFormattedDate()}</strong>
            </div>

            {dailyVerse && (
              <button
                ref={shareButtonRef}
                type="button"
                className="daily-verse-share-main"
                onClick={toggleShareOptions}
                aria-label={languageText.share}
                title={languageText.share}
              >
                <FontAwesomeIcon icon={showShareOptions ? faXmark : faShare} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="daily-verse-loading">
            <div className="daily-verse-spinner"></div>
            <p>{languageText.loading}</p>
          </div>
        ) : dailyVerse ? (
          <div className="daily-verse-body">
            <div className="daily-verse-reference">
              <span className="daily-verse-book">
                {getBookName(getVerseBook())}
              </span>
              <span>{getVerseChapter()}</span>
              <span>:</span>
              <span>{getVerseNumber()}</span>
            </div>

            <p className="daily-verse-text">{getVerseText()}</p>
          </div>
        ) : (
          <div className="daily-verse-loading">
            <p>{languageText.loading}</p>
          </div>
        )}

        {showShareOptions && dailyVerse && (
          <div className="daily-verse-share-options" ref={shareOptionsRef}>
            <button
              type="button"
              className={copied ? "daily-verse-share-copied" : ""}
              onClick={copyVerse}
              aria-label={copied ? languageText.copied : languageText.copy}
              title={copied ? languageText.copied : languageText.copy}
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>

            <button
              type="button"
              onClick={() => shareOnSocialMedia("facebook")}
              aria-label={languageText.facebook}
              title={languageText.facebook}
            >
              <FontAwesomeIcon icon={faFacebook} />
            </button>

            <button
              type="button"
              onClick={() => shareOnSocialMedia("whatsapp")}
              aria-label={languageText.whatsapp}
              title={languageText.whatsapp}
            >
              <FontAwesomeIcon icon={faWhatsapp} />
            </button>

            <button
              type="button"
              onClick={() => shareOnSocialMedia("gmail")}
              aria-label={languageText.gmail}
              title={languageText.gmail}
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </button>

            <button
              type="button"
              onClick={() => shareOnSocialMedia("twitter")}
              aria-label={languageText.twitter}
              title={languageText.twitter}
            >
              <FontAwesomeIcon icon={faTwitter} />
            </button>

            <button
              type="button"
              onClick={() => shareOnSocialMedia("telegram")}
              aria-label={languageText.telegram}
              title={languageText.telegram}
            >
              <FontAwesomeIcon icon={faTelegram} />
            </button>

            <button
              type="button"
              onClick={shareWithDevice}
              aria-label={languageText.other}
              title={languageText.other}
            >
              <FontAwesomeIcon icon={faShareNodes} />
            </button>
          </div>
        )}
      </article>
    </div>
  );
}

export default DailyVerse;